import { useAppContext } from "../../../AppContext";
import { FeatureFlagKey, StudioType, UserRole } from "../../enums";
import { IconType } from "../../icons/Icon";
import { getCurrentUserRole } from "../helpers/userHelper";
import { canManageReviews } from "../../../components/reviews/utils/reviewUtility";
import {
  studioHidesBodyMetrics,
  studioHidesGoals,
  studioHidesPlans,
  studioHidesWorkouts,
} from "../helpers/studioHelper";
import { IUserModel } from "../hooks/useAuthentication";
import { useFeatureFlag } from "../hooks/useFeatureFlag";
import { useSupportUnreadCount } from "../hooks/useSupportUnreadCount";
import { IStudioSelectItemModel } from "../models/studioSelectItemModel";
import { NavigationItems } from "../routes/navigationItems";
import { routePaths } from "../routes/routePaths";

export interface INavigationLink {
  label: string;
  tagLabel?: string;
  alertTagLabel?: string;
  hasUnreadDot?: boolean;
  iconType: IconType;
  iconWidth?: string;
  iconHeight?: string;
  href?: string;
  children?: INavigationLink[];
  navigationItem?: NavigationItems;
  dataOnboarding?: string;
  // LaunchDarkly flag that must be on for the item to show
  featureFlag?: FeatureFlagKey;
  // Function that determines if item should show
  shouldShow?: (
    selectedStudio?: IStudioSelectItemModel,
    currentUser?: IUserModel | null,
    userStudios?: IStudioSelectItemModel[],
  ) => boolean;
}

const normalizePath = (path?: string) =>
  path?.toLowerCase()?.replace(/\/+$/, "");

const isStoreOnlyStudio = (selectedStudio?: IStudioSelectItemModel) =>
  selectedStudio?.types?.length === 1 &&
  selectedStudio.types[0] === StudioType.Store;

export const useGetNavigationLinks = (): INavigationLink[] | null => {
  const {
    currentUser,
    privateRoutes,
    publicRoutes,
    selectedStudio,
    userStudios,
  } = useAppContext();
  const permissions = currentUser?.permissions?.map(normalizePath);

  const supportUnreadCount = useSupportUnreadCount();
  const isGiftVouchersEnabled = useFeatureFlag(FeatureFlagKey.EnableGiftVouchers, false);
  const isReviewsEnabled = useFeatureFlag(FeatureFlagKey.EnableReviewRequests, false);
  const enabledFeatureFlags: Partial<Record<FeatureFlagKey, boolean>> = {
    [FeatureFlagKey.EnableGiftVouchers]: isGiftVouchersEnabled,
    [FeatureFlagKey.EnableReviewRequests]: isReviewsEnabled,
  };
  const isRegularUser = getCurrentUserRole(currentUser) === UserRole.User;

  const isGymOnlyStudio =
    selectedStudio?.types?.length === 1 &&
    selectedStudio.types[0] === StudioType.Gym;

  const navigationItems: INavigationLink[] = [];

  const getVisibleChildren = (
    children: INavigationLink[],
  ): INavigationLink[] => {
    const visibleChildren: INavigationLink[] = [];

    for (const child of children) {
      // Check studio type restrictions using the new flexible approach
      const shouldHideForStudioType = !isNavigationItemAllowed(
        child,
        selectedStudio,
        currentUser,
        userStudios,
        enabledFeatureFlags,
      );

      if (shouldHideForStudioType) {
        continue;
      }

      // A nested group ("Администрация" → "Други") has no page of its own: it shows
      // with the items the user may open, and not at all when none is left
      if (child.children) {
        const nestedChildren = getVisibleChildren(child.children);

        if (nestedChildren.length > 0) {
          visibleChildren.push({ ...child, children: nestedChildren });
        }
        continue;
      }

      const childrenCurrentRoute = privateRoutes.find(
        (x) => x.path === child.href,
      );
      const childEndpoint = normalizePath(
        childrenCurrentRoute?.authenticationEndpoint,
      );

      if (
        permissions?.some(
          (p) =>
            (childEndpoint && childEndpoint.includes(p!)) ||
            (childrenCurrentRoute &&
              !childrenCurrentRoute.authenticationEndpoint),
        )
      ) {
        visibleChildren.push(child);
      }
    }

    return visibleChildren;
  };

  if (
    !currentUser ||
    !currentUser.permissions ||
    currentUser.permissions.length === 0
  ) {
    for (const link of navigationLinks) {
      const currentRoute = publicRoutes.find(
        (x) => x.path === link.href && x.navigationItem === link.navigationItem,
      );

      if (!!currentRoute && !!currentRoute.isPublic) {
        const shouldShowItem = isNavigationItemAllowed(
          link,
          selectedStudio,
          currentUser,
          userStudios,
          enabledFeatureFlags,
        );

        if (shouldShowItem) {
          navigationItems.push(link);
        }
      }
    }
    return removeDuplicateLinksByLabel(navigationItems);
  }

  for (const link of navigationLinks) {
    // "My Workouts" is a regular-user portal tab: it holds trainer-programmed workouts
    // and the subscribed-only "+" create flow. Show it to every regular user — a
    // non-subscriber still sees it, with a greyed-out "+" that explains they need a
    // subscription. (Scoped by role so it never leaks to employees/admins.) "My metrics"
    // is the same kind of tab: staff record a client's metrics on the client's profile.
    // So is "My goals": staff set a client's goals on the client's profile.
    if (
      (link.navigationItem === NavigationItems.MyWorkouts ||
        link.navigationItem === NavigationItems.MyBodyMetrics ||
        link.navigationItem === NavigationItems.MyGoals) &&
      !isRegularUser
    ) {
      continue;
    }

    let currentRoute = privateRoutes.find(
      (x) => x.path === link.href && x.navigationItem === link.navigationItem,
    );

    if (!currentRoute && !!link.href) {
      // Check for public routes, but allow Home to proceed to shouldShow check
      const publicRoute = publicRoutes.find(
        (x) => x.path === link.href && x.navigationItem === link.navigationItem,
      );

      if (!!publicRoute && !!publicRoute.isPublic) {
        if (link.navigationItem !== NavigationItems.PortalCalendar) {
          continue;
        }
        currentRoute = publicRoute;
      }
    }

    const normalizedEndpoint = normalizePath(
      currentRoute?.authenticationEndpoint,
    );

    const resolvedLabel =
      link.navigationItem === NavigationItems.MyReservations && isGymOnlyStudio
        ? "Mоите посещения"
        : link.label;

    const temporaryLink: INavigationLink = {
      label: resolvedLabel,
      tagLabel: link.tagLabel,
      iconWidth: link.iconWidth,
      iconHeight: link.iconHeight,
      hasUnreadDot:
        link.navigationItem === NavigationItems.Support &&
        supportUnreadCount > 0,
      iconType: link.iconType,
      href: link.href,
      navigationItem: link.navigationItem,
      featureFlag: link.featureFlag,
      shouldShow: link.shouldShow,
      dataOnboarding: link.dataOnboarding,
      children: getVisibleChildren(link.children ?? []),
    };

    const hasPermission =
      (permissions &&
        permissions.some(
          (p) => normalizedEndpoint && normalizedEndpoint.includes(p!),
        )) ||
      (!link.children &&
        currentRoute &&
        !currentRoute.authenticationEndpoint &&
        link.href);

    const shouldShowMainItem = isNavigationItemAllowed(
      link,
      selectedStudio,
      currentUser,
      userStudios,
      enabledFeatureFlags,
    );

    // Check if this is a public route being used in authenticated context (like Home)
    const isPublicRouteInAuthenticatedContext =
      currentRoute &&
      publicRoutes.some(
        (x) =>
          x.path === currentRoute.path &&
          x.navigationItem === currentRoute.navigationItem &&
          x.isPublic,
      );

    if (
      shouldShowMainItem &&
      ((temporaryLink.children && temporaryLink.children.length > 0) ||
        hasPermission ||
        isPublicRouteInAuthenticatedContext)
    ) {
      navigationItems.push(temporaryLink);
    }
  }

  return removeDuplicateLinksByLabel(navigationItems);
};

// Helper function to check if navigation item should be shown
const isNavigationItemAllowed = (
  navigationLink: INavigationLink,
  selectedStudio?: IStudioSelectItemModel,
  currentUser?: IUserModel | null,
  userStudios?: IStudioSelectItemModel[],
  enabledFeatureFlags?: Partial<Record<FeatureFlagKey, boolean>>,
): boolean => {
  // A feature behind a LaunchDarkly flag stays hidden until the flag is on; a flag the
  // hook does not read counts as off
  if (
    navigationLink.featureFlag &&
    !enabledFeatureFlags?.[navigationLink.featureFlag]
  ) {
    return false;
  }

  // If shouldShow function exists, use it
  if (navigationLink.shouldShow) {
    return navigationLink.shouldShow(selectedStudio, currentUser, userStudios);
  }

  // If no restrictions defined, show the item
  return true;
};

const navigationLinks: INavigationLink[] = [
  // Client Portal
  {
    label: "Начало",
    iconType: IconType.PlannerIcon,
    navigationItem: NavigationItems.PortalCalendar,
    href: routePaths.portalAppointment,
    shouldShow: (selectedStudio, currentUser) => {
      if (
        !currentUser ||
        !currentUser.permissions ||
        currentUser.permissions.length === 0
      ) {
        return true;
      }
      const userRole = getCurrentUserRole(currentUser);
      return userRole === UserRole.User; // Regular users only
    },
  },
  {
    label: "Начало",
    iconType: IconType.Graph,
    navigationItem: NavigationItems.Dashboard,
    href: routePaths.dashboard,
    dataOnboarding: "nav-dashboard",
  },
  {
    label: "Моите планове",
    iconType: IconType.PlansIcon,
    navigationItem: NavigationItems.MyPlans,
    href: routePaths.portalMyPlans,
    shouldShow: (selectedStudio) => !studioHidesPlans(selectedStudio),
  },
  {
    label: "Mоите резервации",
    iconType: IconType.Yoga,
    navigationItem: NavigationItems.MyReservations,
    href: routePaths.portalMyReservations,
  },
  {
    label: "Моите тренировки",
    iconType: IconType.Dumbell,
    navigationItem: NavigationItems.MyWorkouts,
    href: routePaths.portalMyWorkouts,
    shouldShow: (selectedStudio) => !studioHidesWorkouts(selectedStudio),
  },
  {
    label: "Моите метрики",
    iconType: IconType.Statistics,
    navigationItem: NavigationItems.MyBodyMetrics,
    href: routePaths.portalMyBodyMetrics,
    shouldShow: (selectedStudio) => !studioHidesBodyMetrics(selectedStudio),
  },
  {
    label: "Моите цели",
    iconType: IconType.Trophy,
    navigationItem: NavigationItems.MyGoals,
    href: routePaths.portalMyGoals,
    shouldShow: (selectedStudio) => !studioHidesGoals(selectedStudio),
  },
  {
    label: "Mоето семейство",
    iconType: IconType.Family,
    navigationItem: NavigationItems.MyFamily,
    href: routePaths.portalMyFamily,
  },

  // Studio
  {
    label: "Календар",
    iconType: IconType.PlannerIcon,
    navigationItem: NavigationItems.PlannerCalendar,
    href: routePaths.calendar,
    dataOnboarding: "nav-calendar",
    shouldShow: (selectedStudio, currentUser) => {
      if (
        !currentUser ||
        !currentUser.permissions ||
        currentUser.permissions.length === 0
      ) {
        return false; // Hide for unauthenticated users
      }
      const userRole = getCurrentUserRole(currentUser);
      if (userRole === UserRole.User) {
        return false; // Employees and higher roles only
      }
      return (
        (selectedStudio?.types?.includes(StudioType.Group) ?? false) ||
        (selectedStudio?.types?.includes(StudioType.Service) ?? false)
      );
    },
  },
  {
    label: "Зала",
    iconType: IconType.Dumbell,
    navigationItem: NavigationItems.Gym,
    href: routePaths.gym,
    shouldShow: (selectedStudio) =>
      selectedStudio?.types?.includes(StudioType.Gym) ?? false,
  },
  {
    label: "Каса",
    iconType: IconType.CashRegister,
    navigationItem: NavigationItems.Cashdesk,
    href: routePaths.cashdesk,
  },
  {
    label: "Зареждане на карта",
    iconType: IconType.CardRefillIcon,
    navigationItem: NavigationItems.CardRefill,
    href: routePaths.userRefillCard,
    shouldShow: (selectedStudio) =>
      !isStoreOnlyStudio(selectedStudio) && !studioHidesPlans(selectedStudio),
  },
  {
    label: "Репорти",
    iconType: IconType.Statistics,
    navigationItem: NavigationItems.Statistics,
    href: routePaths.statistics,
    dataOnboarding: "nav-statistics",
  },

  {
    label: "Инвентар",
    iconType: IconType.Inventory,
    children: [
      {
        label: "Наличности",
        iconType: IconType.Graph,
        navigationItem: NavigationItems.InventoryOverview,
        href: routePaths.inventoryOverview,
      },
      {
        label: "Продукти",
        iconType: IconType.Products,
        navigationItem: NavigationItems.InventoryProducts,
        href: routePaths.inventoryProducts,
      },
      {
        label: "Доставки",
        iconType: IconType.Delivery,
        navigationItem: NavigationItems.InventoryTransfers,
        href: routePaths.inventoryTransfers,
      },
    ],
  },

  {
    label: "Администрация",
    iconType: IconType.AdministrationIcon,
    children: [
      {
        label: "Потребители",
        iconType: IconType.UsersIcon,
        navigationItem: NavigationItems.Users,
        href: routePaths.users,
        dataOnboarding: "nav-users",
      },
      {
        label: "Планове",
        iconType: IconType.PlansIcon,
        navigationItem: NavigationItems.Plans,
        href: routePaths.plans,
        dataOnboarding: "nav-plans",
        shouldShow: (selectedStudio) =>
          !isStoreOnlyStudio(selectedStudio) &&
          !studioHidesPlans(selectedStudio),
      },
      {
        label: "Семейства",
        iconType: IconType.Family,
        navigationItem: NavigationItems.Families,
        href: routePaths.families,
        dataOnboarding: "nav-families",
        shouldShow: (selectedStudio) => !isStoreOnlyStudio(selectedStudio),
      },
      {
        label: "Служители",
        iconType: IconType.EmployeesIcon,
        navigationItem: NavigationItems.Employees,
        href: routePaths.employees,
        dataOnboarding: "nav-employees",
      },
      {
        label: "Подаръчни ваучери",
        iconType: IconType.Gift,
        navigationItem: NavigationItems.GiftVouchers,
        href: routePaths.giftVouchers,
        featureFlag: FeatureFlagKey.EnableGiftVouchers,
      },
      {
        label: "Ревюта",
        iconType: IconType.Star,
        // The star fills its whole box, so at the default 34px it looks bigger than the rest.
        iconWidth: "26px",
        iconHeight: "26px",
        navigationItem: NavigationItems.StudioReviews,
        href: routePaths.studioReviews,
        featureFlag: FeatureFlagKey.EnableReviewRequests,
        shouldShow: (_selectedStudio, currentUser) =>
          canManageReviews(getCurrentUserRole(currentUser)),
      },
      // The pages set up once and rarely opened again, so the daily ones above stay short
      {
        label: "Други",
        iconType: IconType.Apps,
        children: [
          {
            label: "Обекти",
            iconType: IconType.StudioIcon,
            navigationItem: NavigationItems.Studios,
            href: routePaths.studios,
            dataOnboarding: "nav-studios",
          },
          {
            label: "Типове планове",
            iconType: IconType.StackedCards,
            navigationItem: NavigationItems.PlanTypes,
            href: routePaths.nomenclaturePlanTypes,
            dataOnboarding: "nav-plan-types",
            shouldShow: (selectedStudio) =>
              selectedStudio?.studioUsesDifferentPlanTypes === true &&
              !studioHidesPlans(selectedStudio),
          },
          {
            label: "Форми",
            iconType: IconType.File,
            navigationItem: NavigationItems.Forms,
            href: routePaths.forms,
            dataOnboarding: "nav-forms",
            shouldShow: (selectedStudio) =>
              selectedStudio?.usesPreReservationForms === true,
          },
          {
            label: "Зали",
            iconType: IconType.Store2,
            navigationItem: NavigationItems.Halls,
            href: routePaths.nomenclatureHalls,
            dataOnboarding: "nav-halls",
            shouldShow: (selectedStudio, currentUser) =>
              selectedStudio?.tenantUsesMoreThanOneHall ?? false,
          },
          {
            label: "Типове блокирания",
            iconType: IconType.BlockedStop,
            navigationItem: NavigationItems.BreakTypes,
            href: routePaths.nomenclatureBreakType,
            shouldShow: (selectedStudio) =>
              selectedStudio?.types?.includes(StudioType.Service) ?? false,
          },
          {
            label: "Типове услуги",
            iconType: IconType.Scissors,
            navigationItem: NavigationItems.ServiceTypes,
            href: routePaths.nomenclatureServiceType,
            dataOnboarding: "nav-service-types",
            shouldShow: (selectedStudio) =>
              selectedStudio?.types?.includes(StudioType.Service) ?? false,
          },
          {
            label: "Категории за класове",
            iconType: IconType.Dumbell,
            navigationItem: NavigationItems.EventTypeCategories,
            href: routePaths.nomenclatureEventTypeCategory,
            dataOnboarding: "nav-class-categories",
            shouldShow: (selectedStudio) =>
              (selectedStudio?.types?.includes(StudioType.Group) ?? false) &&
              selectedStudio?.useGroupClassCategories === true,
          },
          {
            label: "Свързани обекти",
            iconType: IconType.StudioRelations,
            navigationItem: NavigationItems.StudioRelations,
            href: routePaths.studioRelations,
            shouldShow: (_selectedStudio, _currentUser, userStudios) =>
              (userStudios?.length ?? 0) > 1,
          },
          {
            label: "Бизнес клиенти",
            iconType: IconType.DealIcon,
            navigationItem: NavigationItems.Tenants,
            href: routePaths.tenants,
          },
          {
            label: "SMS | Email логове",
            iconType: IconType.Envelope2,
            navigationItem: NavigationItems.CommunicationLogs,
            href: routePaths.communicationLogs,
            dataOnboarding: "nav-comm-logs",
          },
        ],
      },
    ],
  },

  {
    label: "Поддръжка",
    iconType: IconType.Information,
    navigationItem: NavigationItems.Support,
    href: routePaths.support,
    dataOnboarding: "nav-support",
  },

  {
    label: "Вход или регистрация",
    iconType: IconType.Login,
    navigationItem: NavigationItems.Login,
    href: routePaths.login,
    shouldShow: (selectedStudio, currentUser) => {
      // Show Login only for unauthenticated users (no permissions)
      return (
        !currentUser ||
        !currentUser.permissions ||
        currentUser.permissions.length === 0
      );
    },
  },
];

const removeDuplicateLinksByLabel = (
  links: INavigationLink[],
): INavigationLink[] => {
  const seen = new Set<string>();
  return links.filter((link) => {
    if (seen.has(link.label)) return false;
    seen.add(link.label);
    return true;
  });
};
